import { makeEvent } from "~/lib/signals";
import { moodIco } from "~/util/activities";
import { icon, node } from "~/util/dom";
import MyMapsContextMenu from "~/es6/mymaps.contextmenu";
import { _ } from "~/i18n";
import MyMapsShare from "~/es6/mymaps.share";
import Scene from "~/scene";
import { store } from "~/state/store";
import MyMapsEditActivity from "~/ui/dialogs/mymaps-edit-activity";
import MyMapsLinkActivity from "~/ui/dialogs/mymaps-link-activity";
import { standardDistanceFormat, standardTimeFormat } from "~/util/format";
import createSDSIcon from "~/ui/icon";
import { activityTypeToIcon } from "./util";
import { getNoteVersion, isPremiumNoteUser } from "~/util/user-note";
import MyMapsExport from "~/ui/dialogs/mymaps-export";
import MyMapsRemove from "~/ui/dialogs/mymaps-remove";
import type ActivitiesList from "./list";

function formatDate(dateInput) {
	const date = new Date(dateInput * 1000);
	const keywords = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

	return `${_(`calendar.shortDays.${keywords[date.getDay()]}`)} ${[date.getDate(), date.getMonth() + 1, date.getFullYear()].join(". ")}`;
}
export default class ActivitiesListItem {
	_isLeft: boolean;

	_owner: ActivitiesList;

	_dom: {
		container: HTMLLIElement;
		menuButton: HTMLButtonElement;
		header: HTMLDivElement;
	};

	_contextMenu: MyMapsContextMenu;

	_data: any;

	_coords: Array<any>;

	_ss: Array<any>;

	_printEls: Array<HTMLSpanElement | SVGElement>;

	constructor(data, owner, isLeft: boolean) {
		this._data = data;
		this._owner = owner;
		this._dom = {
			container: node("li"),
			menuButton: node("button", { type: "button", className: "menu" }),
			header: node("div", { className: "header" }),
		};
		this._ss = [];
		this._printEls = [];
		this._isLeft = isLeft;

		let coords = [];

		this._data.data?.data?.forEach(geom => {
			coords = coords.concat(Scene.stringToCoords(geom.route));
		});

		this._coords = coords;

		this._build();
	}

	get container() {
		return this._dom.container;
	}

	get coords() {
		return this._coords;
	}

	get id() {
		return this._data.id;
	}

	destroy() {
		this._dom.container.removeEventListener("click", this, false);
		this._dom.container.removeEventListener("mouseover", this, false);
		this._dom.container.removeEventListener("mouseout", this, false);
	}

	handleEvent(event) {
		event.preventDefault();

		switch (event.type) {
			case "click":
				if (this._contextMenu && this._contextMenu.container.contains(event.target)) {
					return;
				}
				if (event.target === this._dom.menuButton || event.target.closest("button.menu")) {
					event.stopPropagation();
					this._createContextMenu();
					this._contextMenu.toggle();
				} else if (event.target.closest(".icon-public") || event.target.classList.contains("icon-public")) {
					this._openSharePopup();
				} else {
					this._open();
				}
				break;

			case "mouseover":
				this._owner.highlight(this._data.id);
				break;

			case "mouseout":
				this._owner.highlight();
				break;

			default:
				//
		}
	}

	print() {
		if (this._printEls.length) {
			return;
		}

		if (this._data.mood) {
			const mood = node("div", { className: "mood" });

			mood.appendChild(icon(moodIco(this._data.mood)));
			this._dom.header.appendChild(mood);

			this._printEls.push(mood);
		}

		// pro tisk
		const printImg = this._data.img ? node("img", { src: Mapy.CDN.setSize(this._data.img, Mapy.CDN.SIZE_400_225), crossOrigin: "anonymous" }) : icon("activities");

		printImg.classList.add("print-img");
		this._dom.header.insertBefore(printImg, this._dom.header.firstChild);

		this._printEls.push(printImg);
	}

	printDone() {
		while (this._printEls.length) {
			const el = this._printEls.pop();

			el.remove();
		}
	}

	_build() {
		const img = node("div", { className: "img" });
		const distance = standardDistanceFormat(this._data.data.info.totalLength);
		const formatTime = standardTimeFormat(this._data.data.info.totalTime, true);
		const summary = node("p", {}, `${distance.distance} ${distance.unit} • ${formatTime.time} ${formatTime.unit}`);
		const title = node("p", {}, this._data.usertitle && this._data.usertitle !== "" ? this._data.usertitle : this._data.title);
		const date = node("span", {}, formatDate(this._data.user_time ? Math.floor(this._data.user_time / 1000) : this._data.data.info.startTime));

		this._dom.menuButton = node("button", { type: "button", className: "menu" });
		this._dom.container.title = Mapy.Util.escapeHTML(this._data.usertitle && this._data.usertitle !== "" ? this._data.usertitle : this._data.title);

		img.appendChild(node("img", { src: this._data.img ? Mapy.CDN.setSize(this._data.img, Mapy.CDN.SIZE_176_100) : "/img/mymap/folder-placeholder-detail.png", crossOrigin: "anonymous" }));

		this._dom.menuButton.appendChild(icon("etc-vertical"));
		summary.insertBefore(createSDSIcon({ symbol: activityTypeToIcon(this._data.data.info.trackType) }), summary.firstChild);
		this._dom.header.appendChild(summary);
		this._dom.container.appendChild(img);
		this._dom.container.appendChild(this._dom.header);
		this._dom.container.appendChild(title);
		this._dom.container.appendChild(date);
		this._addPublicIcon(this._data.public);
		this._dom.container.appendChild(this._dom.menuButton);

		this._dom.container.addEventListener("click", this, false);
		this._dom.container.addEventListener("mouseover", this, false);
		this._dom.container.addEventListener("mouseout", this, false);
	}

	_createContextMenu() {
		if (this._contextMenu) {
			return;
		}

		this._contextMenu = new MyMapsContextMenu({
			clickCb: action => this._contextMenuAction(action),
			mapyboxItem: this._data,
			place: "activities-list",
		});

		this._contextMenu.parent = this._dom.container;
		this._contextMenu.closestSelector = "button.menu";

		if (this._isLeft) {
			this._contextMenu.container.classList.add("left-item");
		}
	}

	_open() {
		this._owner.open(this._data.id);
	}

	highlight(show) {
		this._dom.container.classList[show ? "add" : "remove"]("hover");
		makeEvent("highlight-activities-list-item", this, { show, coords: this._coords[0] });
	}

	_contextMenuAction(action) {
		switch (action) {
			case "export":
				new MyMapsExport({
					data: {
						name: "activity",
						data: {
							mid: this._data.id,
							title: this._data.usertitle || this._data.title,
						},
					},
				}).open();
				break;

			case "share":
				this._openSharePopup();
				break;
			case "link-to-folder":
			case "link-to-another-folder":
				new MyMapsLinkActivity({
					isPremiumUser: isPremiumNoteUser(),
					noteVersion: getNoteVersion(),
					trackData: this._data,
					type: action,
				}).open();
				break;
			case "edit":
				new MyMapsEditActivity({
					isPremiumUser: isPremiumNoteUser(),
					noteVersion: getNoteVersion(),
					data: this._data,
					onSave: editData => {
						Object.assign(this._data, editData);
						this._updateData();
					},
					onError: () => {},
				}).open();
				break;
			case "delete":
			case "delete-from-all":
				new MyMapsRemove({
					data: this._data,
					operation: "delete",
					onRemove: () => {
						this._updateData();
					},
				}).open();
				break;

			default:
				//
		}
	}

	_updateData() {
		this._owner.loadData(true, true);
	}

	_dataToEdit() {
		return {
			title: this._data.usertitle && this._data.usertitle !== "" ? this._data.usertitle : this._data.title,
			id: this._data.id,
			description: this._data.description,
			type: this._data.data.info.trackType,
			mood: this._data.mood,
			public: this._data.public,
		};
	}

	_openSharePopup() {
		const { mapset, provider } = store.getState().map;
		const sharePopup = new MyMapsShare({
			id: this._data.id,
			title: this._data.usertitle && this._data.usertitle !== "" ? this._data.usertitle : this._data.title,
			type: this._data.type,
			url: `${location.protocol}//${location.host}/${mapset}?moje-mapy&cat=aktivity&mid=${this._data.id}${provider === "m3d" ? "&m3d=1" : ""}`,
			public: this._data.public,
			like: true,
		}, cbData => {
			if (cbData.name === "popup-close") {
				this._data.public = cbData.public;
				this._addPublicIcon(this._data.public);

				if (this._data.public) {
					Mapy.getComponent("logger").log("click", {
						text: "sdílet",
						origin: "my_maps",
						type: "acctivity",
					});
				}
			}
		});

		sharePopup.open();
	}

	_addPublicIcon(pub) {
		if (pub) {
			this._dom.container.appendChild(icon("public"));
		} else {
			this._dom.container.querySelector(".icon-public")?.remove();
		}
	}
}
